import React, { useRef, useState } from "react";
import EditableInput from "./EditableInput";
import TooltipWrapper from "./TooltipWrapper";
import { LongTextCell } from "./LongTextCell";
import { Link } from "react-router-dom";
import { RichDropDownCell } from "./RichDropDownCell";
import { DropDownCell } from "./DropDownCell";
import { FilesColumnCell } from "./FilesColumnCell";
import { ChartCell } from "./ChartCell";
import HoverImage from "../../products/HoverImage";
import { formatValue } from "../../../utils/utils";
import { format } from "date-fns";
import { cn } from "../../../lib/utils";
import { useSelector } from "react-redux";
import { getIsError } from "../../../utils/getIsError";
import { useDispatch } from "react-redux";
import { setErrorFields } from "../../../store/tableSlice";
import useOutsideClick from "../../../hooks/useOutsideClick";
import { DateTimeCell } from "./DateTimeCell";
import { Pencil } from "lucide-react";

const Cell = ({
  info,
  columnInfo,
  editConfig = {},
  navigationConfig = {},
  headerData,
}) => {
  // Safely get value and handle potential non-extensible objects
  const value = React.useMemo(() => {
    try {
      return info?.getValue();
    } catch (error) {
      console.warn("Error getting cell value:", error);
      return undefined;
    }
  }, [info]);

  const [isEditing, setIsEditing] = useState(false);
  const [editedValue, setEditedValue] = useState(value);
  const [isLoading, setIsLoading] = useState(false);
  const errorFields = useSelector((state) => state.table.errorFields);

  const [optimisticValue, setOptimisticValue] = useState(value);
  const dispatch = useDispatch();

  const cellRef = useRef(null);

  const formattableTypes = ["currency", "float", "int", "integer"];

  const row = info.row;
  // Safely access original row data to prevent extensibility errors
  const originalRow = React.useMemo(() => {
    try {
      return { ...row.original };
    } catch (error) {
      console.warn("Error accessing row data:", error);
      return row.original;
    }
  }, [row.original]);

  const tableInstance = info.table;
  let isEditable;

  // Blacklist of fields that should never be editable (like IDs)
  // const nonEditableFields = ["id", "product_id", "campaign_id"];
  // const isBlacklisted =
  //   nonEditableFields.includes(columnInfo.key) ||
  //   originalRow[columnInfo.key]?.tip_text?.toLowerCase().includes("id");

  // if the ad is already launched, we don't want to allow any edits
  if (
    originalRow?.["status"] &&
    originalRow["status"]?.display === "launched"
  ) {
    isEditable = false;
    // } else if (isBlacklisted) {
    //   // Never allow editing of ID fields, even if marked as editable
    //   isEditable = false;
  } else {
    isEditable =
      originalRow[columnInfo.key]?.editable || editConfig.forceEditable;
  }

  const tipText = originalRow[columnInfo.key]?.tip_text;
  const maxStringLength = originalRow?.[columnInfo.key]?.string_len;

  const handleSave = async () => {
    // Don't save if not editing or no changes made
    if (!isEditing) return;

    // 1. storing the original value for rollback
    const originalValue = value;

    setIsEditing(false);
    setOptimisticValue(editedValue);

    try {
      const result = await editConfig.onSave?.(columnInfo, row, editedValue);

      // 3. if the save fails, rollback the value
      if (result?.error) {
        setOptimisticValue(originalValue);
        setEditedValue(originalValue); // Also reset the edited value
      }
    } catch (error) {
      // 4. if the save fails due to unexpected error, also rollback the value
      setEditedValue(originalValue);
      setOptimisticValue(originalValue);
      console.log("Error saving cell: ", error);
    }
  };

  // Use the outside click hook to auto-save when clicking outside
  const editingRef = useOutsideClick(handleSave, isEditing);

  const isRequired = headerData?.required_fields?.includes(columnInfo.key);
  const isError = getIsError(errorFields, row.id, columnInfo.key);
  const isEmpty = value === undefined || value === null || value === "";

  switch (columnInfo.type) {
    case "long_text":
      return (
        <LongTextCell
          setOptimisticValue={setOptimisticValue}
          optimisticValue={optimisticValue}
          maxStringLength={maxStringLength}
          rowId={row.id}
          columnId={columnInfo.key}
          value={value}
          isEditable={isEditable}
          onSave={async (newValue) => {
            return editConfig.onSave?.(columnInfo, row, newValue);
          }}
          onCancel={() => {
            editConfig.onCancel?.(originalRow);
          }}
          editConfig={editConfig}
          columnInfo={columnInfo}
          isError={isError}
          isRequired={isRequired}
          isEmpty={isEmpty}
          errorFields={errorFields}
        />
      );
    case "rich_dropdown": {
      const dropDownData = originalRow[columnInfo.key];

      const data = {
        values: dropDownData.values || [],
        selected: dropDownData.selected || null,
      };

      return (
        <RichDropDownCell
          setOptimisticValue={setOptimisticValue}
          optimisticValue={optimisticValue}
          isEditable={isEditable}
          data={data}
          onUpdate={(originalRow, { selected }) => {
            return editConfig.onSave?.(columnInfo, row, selected);
          }}
          rowId={row.id}
          originalRow={originalRow}
          columnInfo={columnInfo}
          isError={isError}
          isRequired={isRequired}
          isEmpty={isEmpty}
          errorFields={errorFields}
        />
      );
    }
    case "dropdrown":
    case "dropdown": {
      const dropDownData = originalRow[columnInfo.key];
      const data = {
        values: dropDownData?.values || [],
        selected: dropDownData?.multiple
          ? dropDownData.selected ?? []
          : dropDownData?.selected,
        multiple: dropDownData?.multiple || false,
        allowCreation: dropDownData?.allow_creation || false,
        editable: dropDownData?.editable || false,
      };

      return (
        <DropDownCell
          setOptimisticValue={setOptimisticValue}
          optimisticValue={optimisticValue}
          isEditable={isEditable}
          data={data}
          onUpdate={(newData) => {
            if (editConfig.onSave) {
              editConfig.onSave(columnInfo, row, newData.selected);
            }
          }}
          row={row}
          columnInfo={columnInfo}
          isError={isError}
          isRequired={isRequired}
          isEmpty={isEmpty}
          errorFields={errorFields}
        />
      );
    }
    case "files": {
      const filesData = originalRow[columnInfo.key];

      const data = {
        files: filesData?.files || [],
        limit: filesData?.limit || 3,
      };

      return (
        <FilesColumnCell
          isEditable={isEditable}
          row={row}
          data={data}
          onUpdate={(files) => {
            editConfig.onSave(columnInfo, row, files);
          }}
          isLoading={editConfig.isLoading?.(row.id) || false}
          columnInfo={columnInfo}
          isError={isError}
          isRequired={isRequired}
          isEmpty={isEmpty}
          errorFields={errorFields}
        />
      );
    }
    case "image": {
      const rawValue = info.getValue();
      return (
        <HoverImage
          rowId={row.id}
          columnId={columnInfo.key}
          src={rawValue}
          alt="No preview"
        />
      );
    }
    case "bool": {
      const rawValue = info.getValue();

      // Handle null/undefined cases
      if (rawValue === null || rawValue === undefined) {
        return headerData[columnInfo.key].display_null;
      }

      // Convert to string and normalize for comparison
      const normalizedValue = rawValue.toString().toLowerCase();

      // Handle different boolean representations
      if (normalizedValue === "1" || normalizedValue === "true") {
        return headerData[columnInfo.key].display_true;
      } else if (normalizedValue === "0" || normalizedValue === "false") {
        return headerData[columnInfo.key].display_false;
      }

      // Return null display value for invalid inputs
      return headerData[columnInfo.key].display_null;
    }
    case "datetime":
    case "date": {
      return (
        <DateTimeCell
          value={value}
          isEditable={isEditable}
          onSave={(newValue) => {
            return editConfig.onSave?.(columnInfo, row, newValue);
          }}
          columnInfo={columnInfo}
          isError={isError}
          isRequired={isRequired}
          isEmpty={isEmpty}
        />
      );
    }
    case "chart": {
      const chartData = originalRow[columnInfo.key];
      return (
        <ChartCell data={chartData} columnInfo={columnInfo} rowId={row.id} />
      );
    }
    case "uri":
    case "url": {
      const urlValue = info.getValue();

      if (!urlValue || urlValue === "") {
        return (
          <div className="flex items-center gap-1 w-full min-w-0">
            <span className="max-w-full break-words text-gray-400 flex-1 min-w-0">
              {columnInfo.display || "N/A"}
            </span>
          </div>
        );
      }

      return (
        <div className="flex items-center gap-1 w-full min-w-0">
          <a
            href={urlValue}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="break-words text-blue-600 underline underline-offset-2 hover:text-blue-800 flex-1 min-w-0"
          >
            {urlValue}
          </a>
        </div>
      );
    }
    case "launch_status": {
      const rawValue = info.getValue()?.toString().toLowerCase();
      const columnInfo = info.column.columnDef.meta.columnInfo;

      if (!rawValue || !columnInfo) return "";

      const statusDisplayMap = {
        // hack applied for now, should be "launched"
        launch: columnInfo.display_launch,
        launched: columnInfo.display_launched,
        on_hold: columnInfo.display_on_hold,
        error: columnInfo.display_error,
      };

      const displayValue = statusDisplayMap[rawValue] || rawValue;

      return displayValue;
    }
    default: {
      let displayContent;

      if (value === undefined || value === null || value === "") {
        // If empty value, show the placeholder without link wrapping, but add pencil icon if editable
        displayContent = (
          <div className="flex items-center gap-1 w-full min-w-0">
            <span className="max-w-full break-words text-gray-400 flex-1 min-w-0">
              {columnInfo.display || "N/A"}
            </span>
            {(isEditable || columnInfo.type === "uri") && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsEditing(true);
                }}
                className="flex-shrink-0 opacity-0 group-hover:opacity-100 hover:opacity-100 p-1 rounded hover:bg-gray-200 transition-all duration-200"
                title="Edit this field"
              >
                <Pencil className="w-3 h-3 text-gray-500 hover:text-gray-700" />
              </button>
            )}
          </div>
        );
      } else {
        // For actual values, apply formatting and possibly navigation
        const shouldFormat = formattableTypes.includes(columnInfo.type);

        const formattedValue = shouldFormat
          ? formatValue(optimisticValue, columnInfo.type, columnInfo)
          : optimisticValue;

        displayContent = navigationConfig.navigable ? (
          <div className="flex items-center gap-1 w-full min-w-0">
            <Link
              to={
                navigationConfig.getUrl({
                  originalRow,
                  optimisticValue,
                  columnInfo,
                })?.pathname
              }
              state={
                navigationConfig.getUrl({
                  originalRow,
                  optimisticValue,
                  columnInfo,
                })?.state
              }
              target={navigationConfig?.target}
              onClick={(e) => e.stopPropagation()}
              className="break-words text-blue-600 underline underline-offset-2 hover:text-blue-800 flex-1 min-w-0"
            >
              {formattedValue}
            </Link>
            {(isEditable || columnInfo.type === "uri") && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsEditing(true);
                }}
                className="flex-shrink-0 opacity-0 group-hover:opacity-100 hover:opacity-100 p-1 rounded hover:bg-gray-200 transition-all duration-200"
                title="Edit this field"
              >
                <Pencil className="w-3 h-3 text-gray-500 hover:text-gray-700" />
              </button>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-1 w-full min-w-0">
            <span className="break-words w-[165px] xl:w-full flex-1 min-w-0">
              {formattedValue}
            </span>
            {(isEditable || columnInfo.type === "uri") && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsEditing(true);
                }}
                className="flex-shrink-0 opacity-0 group-hover:opacity-100 hover:opacity-100 p-1 rounded hover:bg-gray-200 transition-all duration-200"
                title="Edit this field"
              >
                <Pencil className="w-3 h-3 text-gray-500 hover:text-gray-700" />
              </button>
            )}
          </div>
        );
      }

      let cellContent;
      if ((isEditable || columnInfo.type === "uri") && isEditing) {
        cellContent = (
          <EditableInput
            ref={editingRef} // Pass the outside click ref to EditableInput
            maxStringLength={maxStringLength}
            value={editedValue}
            // 2. updating the optimistic value, which will be reflected in the display value after input is closed
            editConfig={{
              ...editConfig,
              onChange: (value) => {
                setEditedValue(value);
                if (value) {
                  const updatedFields =
                    errorFields
                      .find((error) => error.rowId === row.id)
                      ?.fields.filter((field) => field !== columnInfo.key) ||
                    [];

                  dispatch(
                    setErrorFields(
                      errorFields.map((error) => {
                        if (error.rowId === row.id) {
                          return { ...error, fields: updatedFields };
                        }
                        return error;
                      })
                    )
                  );
                } else {
                  const existingError = errorFields.find(
                    (error) => error.rowId === row.id
                  );

                  if (existingError) {
                    const updatedFields = Array.from(
                      new Set([...existingError.fields, columnInfo.key])
                    );
                    dispatch(
                      setErrorFields(
                        errorFields.map((error) =>
                          error.rowId === row.id
                            ? { ...error, fields: updatedFields }
                            : error
                        )
                      )
                    );
                  } else {
                    dispatch(
                      setErrorFields([
                        ...errorFields,
                        {
                          rowId: row.id,
                          fields: [columnInfo.key],
                        },
                      ])
                    );
                  }
                }
              },
            }}
            onSave={handleSave}
            tableInstance={tableInstance}
            columnId={columnInfo.key}
            cellRef={cellRef}
            className="no-underline text-gray-900"
          />
        );
      } else {
        // If editable, wrap with clickable container (but not if it's also navigable, as we handle that separately)
        if (
          (isEditable || columnInfo.type === "uri") &&
          !navigationConfig.navigable
        ) {
          cellContent = (
            <div
              ref={cellRef}
              className="cursor-pointer hover:border hover:border-blue-300 hover:bg-blue-50 rounded  flex items-center gap-2 h-full"
              onClick={(e) => {
                e.stopPropagation();
                setIsEditing(true);
              }}
            >
              {displayContent}
            </div>
          );
        } else {
          cellContent = displayContent;
        }
      }

      return (
        <div
          className={cn(
            "h-full items-center text-left max-w-full truncate flex group",
            isError && "border border-red-500 bg-red-50"
          )}
        >
          {isRequired && isEmpty && !isEditing && (
            <span
              className="text-red-500 mr-px align-middle"
              title="This is a required field."
            >
              *
            </span>
          )}
          <TooltipWrapper tipText={tipText}>{cellContent}</TooltipWrapper>
        </div>
      );
    }
  }
};

export default Cell;
